import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { orgAPI, affiliationAPI } from '../../services/api';
import { Building2, ShieldCheck, Users, ScrollText, LayoutDashboard, Check, AlertTriangle, UserPlus } from 'lucide-react';
import { Button } from '../../components/ui/button';

const TABS = [
  { key: 'dashboard', label: 'Tablero de Sección', icon: LayoutDashboard },
  { key: 'affiliations', label: 'Afiliaciones', icon: UserPlus },
  { key: 'duplicates', label: 'Duplicados', icon: AlertTriangle },
  { key: 'sections', label: 'Secciones', icon: Building2 },
  { key: 'matrix', label: 'Roles y Permisos', icon: ShieldCheck },
  { key: 'assignments', label: 'Asignaciones', icon: Users },
  { key: 'audit', label: 'Auditoría', icon: ScrollText },
];

const AdminOrganizacion = () => {
  const [ctx, setCtx] = useState(null);
  const [tab, setTab] = useState('dashboard');
  const [activeSection, setActiveSection] = useState('');
  const [error, setError] = useState('');

  const [sections, setSections] = useState([]);
  const [roles, setRoles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [dash, setDash] = useState(null);
  const [newSec, setNewSec] = useState({ name: '', abbreviation: '', state: '' });
  const [affReqs, setAffReqs] = useState([]);
  const [affDash, setAffDash] = useState(null);
  const [qroDash, setQroDash] = useState(null);
  const [activeBucket, setActiveBucket] = useState(null);
  const [bucketDetail, setBucketDetail] = useState([]);
  const [dups, setDups] = useState([]);
  const [cotejo, setCotejo] = useState(null);

  useEffect(() => {
    orgAPI.myContext()
      .then((c) => {
        setCtx(c);
        const secs = c.accessible_sections || [];
        if (secs.length) setActiveSection(secs[0].id);
      })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!ctx) return;
    orgAPI.getSections().then(setSections).catch(() => {});
    orgAPI.getRoles().then(setRoles).catch(() => setRoles([]));
    orgAPI.getAssignments().then(setAssignments).catch(() => setAssignments([]));
    orgAPI.getAudit().then(setAudit).catch(() => setAudit([]));
  }, [ctx]);

  useEffect(() => {
    if (activeSection) orgAPI.getSectionDashboard(activeSection).then(setDash).catch(() => setDash(null));
  }, [activeSection]);

  useEffect(() => {
    if (!ctx) return;
    setActiveBucket(null); setBucketDetail([]);
    affiliationAPI.qroDashboard(activeSection || undefined).then(setQroDash).catch(() => setQroDash(null));
    affiliationAPI.duplicates(activeSection || undefined).then(setDups).catch(() => setDups([]));
  }, [ctx, activeSection]);

  const openBucket = async (key) => {
    setActiveBucket(key);
    try { setBucketDetail(await affiliationAPI.adminBucket(key, activeSection || undefined)); }
    catch { setBucketDetail([]); }
  };

  const openCotejo = async (id) => {
    try { setCotejo(await affiliationAPI.duplicateCotejo(id)); } catch (e) { setError(e.message); }
  };

  const resolveDup = async (id) => {
    try {
      await affiliationAPI.resolveDuplicate(id, 'Resuelto desde panel');
      setCotejo(null);
      setDups(await affiliationAPI.duplicates(activeSection || undefined));
    } catch (e) { setError(e.message); }
  };

  useEffect(() => {
    if (!ctx) return;
    affiliationAPI.adminList().then(setAffReqs).catch(() => setAffReqs([]));
    affiliationAPI.adminDashboard(activeSection || undefined).then(setAffDash).catch(() => setAffDash(null));
  }, [ctx, activeSection]);

  const permCols = Array.from(new Set(roles.flatMap((r) => r.permissions))).sort();

  const createSection = async () => {
    try {
      await orgAPI.createSection(newSec);
      setNewSec({ name: '', abbreviation: '', state: '' });
      setSections(await orgAPI.getSections());
    } catch (e) { setError(e.message); }
  };

  if (error && !ctx) {
    return (
      <AdminLayout>
        <div data-testid="org-error" className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-amber-800">
          <AlertTriangle className="inline h-5 w-5 mr-2" />
          Inicia sesión con una cuenta autorizada para ver la estructura nacional. ({error})
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="org-page" className="space-y-6">
        {/* Header + selector de sección activa */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Estructura Nacional UPIM</h1>
            <p className="text-sm text-gray-500">
              {ctx?.control_total ? 'Control Total' : `Alcance: ${ctx?.accessible_scope || '—'}`}
              {ctx?.source === 'enum_fallback' && ' · (rol heredado temporal)'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Sección activa:</span>
            <select
              data-testid="section-selector"
              value={activeSection}
              onChange={(e) => setActiveSection(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">—</option>
              {(ctx?.accessible_sections || []).map((s) => (
                <option key={s.id} value={s.id}>{s.abbreviation} · {s.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`org-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === t.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Dashboard — tablero calculado desde registros persistidos (tarjetas reconciliadas) */}
        {tab === 'dashboard' && (
          <div data-testid="org-dashboard" className="space-y-4">
            {qroDash ? (
              <>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-lg font-semibold">
                    {qroDash.section ? `${qroDash.section.name} (${qroDash.section.abbreviation})` : 'Consolidado (todas mis secciones)'}
                  </h2>
                  {qroDash.has_synthetic_data && (
                    <span data-testid="synthetic-badge" className="text-[10px] font-bold bg-amber-100 text-amber-800 px-2 py-1 rounded">
                      DATOS SINTÉTICOS DE PRUEBA · {qroDash.synthetic_count}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="qro-cards">
                  {qroDash.cards.map((c) => (
                    <button
                      key={c.key}
                      data-testid={`qro-card-${c.key}`}
                      onClick={() => openBucket(c.key)}
                      className={`text-left bg-white rounded-xl border p-4 transition-colors hover:border-gray-900 ${activeBucket === c.key ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200'}`}
                    >
                      <p className="text-3xl font-bold text-gray-900" data-testid={`qro-count-${c.key}`}>{c.count}</p>
                      <p className="text-xs text-gray-500 mt-1">{c.label}</p>
                    </button>
                  ))}
                </div>

                {activeBucket && (
                  <div className="bg-white rounded-xl border p-5" data-testid="qro-bucket-detail">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold">
                        Detalle: {qroDash.cards.find((c) => c.key === activeBucket)?.label}
                      </h3>
                      <span className="text-xs text-gray-500" data-testid="qro-detail-count">
                        {bucketDetail.length} registro(s) · debe coincidir con la tarjeta ({qroDash.cards.find((c) => c.key === activeBucket)?.count})
                      </span>
                    </div>
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-gray-500"><th className="py-2">Folio</th><th>Nombre</th><th>Estado</th><th>Sección</th><th>Prueba</th></tr></thead>
                      <tbody>
                        {bucketDetail.map((r) => (
                          <tr key={r.id} className="border-t" data-testid="qro-detail-row">
                            <td className="py-2 font-medium">{r.folio}</td>
                            <td>{r.full_name}</td>
                            <td><span className="px-2 py-0.5 rounded bg-gray-100 text-xs">{r.status}</span></td>
                            <td className="text-gray-500 text-xs">{(r.section_id_assigned || r.section_id_requested || '—').slice(0, 8)}</td>
                            <td>{r.is_synthetic ? <span className="text-amber-600 text-xs">sintético</span> : ''}</td>
                          </tr>
                        ))}
                        {bucketDetail.length === 0 && <tr><td colSpan="5" className="py-3 text-gray-500">Sin registros en esta tarjeta.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-500">Selecciona una sección con permiso para ver su tablero.</p>
            )}
          </div>
        )}

        {/* Duplicados — cotejo y resolución auditada con escalamiento Nacional */}
        {tab === 'duplicates' && (
          <div data-testid="org-duplicates" className="space-y-4">
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold mb-3">Coincidencias detectadas</h3>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500"><th className="py-2">Folio</th><th>Nombre</th><th>Email</th><th>Cruce</th><th></th></tr></thead>
                <tbody>
                  {dups.map((r) => (
                    <tr key={r.id} className="border-t" data-testid="dup-row">
                      <td className="py-2 font-medium">{r.folio}</td>
                      <td>{r.full_name}</td>
                      <td className="text-gray-500">{r.email}</td>
                      <td>{r.cross_section ? <span className="text-red-600 text-xs" data-testid="dup-cross">entre secciones → Nacional</span> : <span className="text-gray-500 text-xs">misma sección</span>}</td>
                      <td className="text-right"><Button size="sm" variant="outline" data-testid={`dup-cotejo-${r.id}`} onClick={() => openCotejo(r.id)}>Cotejar</Button></td>
                    </tr>
                  ))}
                  {dups.length === 0 && <tr><td colSpan="5" className="py-3 text-gray-500">Sin duplicados en tu alcance.</td></tr>}
                </tbody>
              </table>
            </div>

            {cotejo && (
              <div className="bg-white rounded-xl border p-5" data-testid="dup-cotejo-panel">
                <h3 className="font-semibold mb-3">Cotejo del folio {cotejo.request.folio}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500">Solicitud</p>
                    <p className="font-medium">{cotejo.request.full_name}</p>
                    <p className="text-sm text-gray-600">{cotejo.request.email} · {cotejo.request.phone}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500">Coincidencias ({cotejo.matches.length})</p>
                    {cotejo.matches.map((m) => (
                      <p key={m.id} className="text-sm text-gray-700">{m.folio} · {m.full_name} · {m.email}</p>
                    ))}
                  </div>
                </div>
                {cotejo.requires_national && (
                  <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm mt-3" data-testid="cotejo-national-note">
                    Coincidencia entre secciones: la resolución requiere UPIM Nacional.
                  </p>
                )}
                <div className="flex gap-2 mt-4">
                  <Button data-testid="dup-resolve-btn" disabled={!cotejo.can_resolve} onClick={() => resolveDup(cotejo.request.id)}>
                    Resolver duplicado
                  </Button>
                  <Button variant="outline" onClick={() => setCotejo(null)}>Cerrar</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Afiliaciones (FASE 2) */}
        {tab === 'affiliations' && (
          <div data-testid="org-affiliations" className="space-y-4">
            {affDash && (
              <div className="bg-white rounded-xl border p-5">
                <h3 className="font-semibold mb-3">Solicitudes por estado {affDash.section_id ? '(sección activa)' : '(alcance completo)'}</h3>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(affDash.by_status || {}).map(([k, v]) => (
                    <div key={k} className="bg-gray-50 rounded-lg px-4 py-3">
                      <p className="text-2xl font-bold text-gray-900">{v}</p>
                      <p className="text-xs text-gray-500">{k}</p>
                    </div>
                  ))}
                  {(!affDash.by_status || Object.keys(affDash.by_status).length === 0) && (
                    <p className="text-gray-500 text-sm">Sin solicitudes en tu alcance.</p>
                  )}
                </div>
              </div>
            )}
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold mb-3">Solicitudes de afiliación</h3>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500"><th className="py-2">Folio</th><th>Nombre</th><th>Tipo</th><th>Estado</th><th>Revisión</th></tr></thead>
                <tbody>
                  {affReqs.map((r) => (
                    <tr key={r.id} className="border-t" data-testid="aff-row">
                      <td className="py-2 font-medium">{r.folio}</td>
                      <td>{r.full_name}</td>
                      <td>{r.applicant_type}</td>
                      <td><span className="px-2 py-0.5 rounded bg-gray-100 text-xs">{r.status}</span></td>
                      <td>{r.needs_section_review ? <span className="text-amber-600 text-xs">sección</span> : ''}{r.duplicate_flag ? <span className="text-red-600 text-xs ml-1">dup</span> : ''}</td>
                    </tr>
                  ))}
                  {affReqs.length === 0 && <tr><td colSpan="5" className="py-3 text-gray-500">Sin solicitudes en tu alcance territorial.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Secciones */}
        {tab === 'sections' && (
          <div data-testid="org-sections" className="bg-white rounded-xl border p-5">
            <table className="w-full text-sm mb-4">
              <thead><tr className="text-left text-gray-500"><th className="py-2">Abrev.</th><th>Nombre</th><th>Estado</th><th>Estatus</th></tr></thead>
              <tbody>
                {sections.map((s) => (
                  <tr key={s.id} className="border-t"><td className="py-2 font-medium">{s.abbreviation}</td><td>{s.name}</td><td>{s.state}</td><td>{s.status}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-wrap gap-2 items-end border-t pt-4">
              <input data-testid="new-section-name" placeholder="Nombre" value={newSec.name} onChange={(e) => setNewSec({ ...newSec, name: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" />
              <input data-testid="new-section-abbr" placeholder="Abrev." value={newSec.abbreviation} onChange={(e) => setNewSec({ ...newSec, abbreviation: e.target.value })} className="border rounded-lg px-3 py-2 text-sm w-24" />
              <input placeholder="Estado" value={newSec.state} onChange={(e) => setNewSec({ ...newSec, state: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" />
              <Button data-testid="create-section-btn" onClick={createSection}>Crear sección</Button>
            </div>
          </div>
        )}

        {/* Matriz roles-permisos */}
        {tab === 'matrix' && (
          <div data-testid="org-matrix" className="bg-white rounded-xl border p-5 overflow-x-auto">
            <table className="text-xs">
              <thead>
                <tr><th className="text-left py-2 pr-4 sticky left-0 bg-white">Rol / Permiso</th>
                  {permCols.map((p) => <th key={p} className="px-1 rotate-0 whitespace-nowrap text-gray-500">{p}</th>)}
                </tr>
              </thead>
              <tbody>
                {roles.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="py-1.5 pr-4 font-medium sticky left-0 bg-white whitespace-nowrap">{r.code}</td>
                    {permCols.map((p) => (
                      <td key={p} className="text-center">{r.permissions.includes(p) ? <Check className="inline h-3.5 w-3.5 text-green-600" /> : ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Asignaciones */}
        {tab === 'assignments' && (
          <div data-testid="org-assignments" className="bg-white rounded-xl border p-5">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500"><th className="py-2">Usuario</th><th>Rol</th><th>Sección</th><th>Estatus</th></tr></thead>
              <tbody>
                {assignments.map((a) => (
                  <tr key={a.id} className="border-t"><td className="py-2 text-gray-600">{a.user_id.slice(0, 8)}…</td><td className="font-medium">{a.role_code}</td><td>{a.section_id ? a.section_id.slice(0, 8) + '…' : 'Nacional'}</td><td>{a.status}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Auditoría */}
        {tab === 'audit' && (
          <div data-testid="org-audit" className="bg-white rounded-xl border p-5">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500"><th className="py-2">Fecha</th><th>Actor</th><th>Acción</th><th>Resultado</th><th>Motivo</th></tr></thead>
              <tbody>
                {audit.map((e) => (
                  <tr key={e.id} className="border-t">
                    <td className="py-2 text-gray-500 whitespace-nowrap">{e.created_at?.slice(0, 19).replace('T', ' ')}</td>
                    <td className="text-gray-600">{e.actor_email}</td>
                    <td>{e.action}</td>
                    <td><span className={e.result === 'denied' ? 'text-red-600' : 'text-green-600'}>{e.result}</span></td>
                    <td className="text-gray-500">{e.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrganizacion;
